import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const DEFAULT_CLIENT_ID = '75040a5c-1a9a-49d5-a061-0fe9087710c9';
//const DEFAULT_CLIENT_ID = 'd994efd8-aad0-4bc5-af4d-443e8536aa38';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = DEFAULT_CLIENT_ID;
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  React.useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const sessionData = {
          token,
          client_id: payload.client_id || clientId,
          auto_login: true,
          extension_number: payload.extension_number || payload.role?.extension_number || null,
          role_id: payload.role_id || payload.role?.id || null,
          role_name: payload.role_name || payload.role?.role_name || null,
          username: payload.username || payload.role?.username || null,
          can_manage_roles: payload.can_manage_roles || payload.role?.can_manage_roles || false
        };
        sessionStorage.setItem('rbac_session', JSON.stringify(sessionData));
        const redirectClientId = payload.client_id || clientId;
        if (redirectClientId) {
          navigate(`/portal/${redirectClientId}/leads`);
        }
      } catch (e) {
        console.error('Token parse error:', e);
      }
    }
  }, [clientId, navigate, searchParams]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const payloadClientId = clientId;
    const trimmedUsername = username.trim();

    const loginPayload = {
      username: trimmedUsername,
      password,
      client_id: payloadClientId
    };

    try {
      const response = await axios.post(`${API_BASE_URL}/rbac/login`, loginPayload);

      const returnedClientId = response?.data?.role?.client_id || payloadClientId;
      if (response?.data?.success || response?.data?.token) {
        const sessionData = {
          token: response.data.token,
          client_id: returnedClientId,
          auto_login: false,
          extension_number: response.data.role?.extension_number || null,
          role_id: response.data.role?.role_id || response.data.role?.id || null,
          role_name: response.data.role?.role_name || null,
          username: response.data.role?.username || null,
          can_manage_roles: response.data.role?.can_manage_roles || false
        };
        sessionStorage.setItem('rbac_session', JSON.stringify(sessionData));
        navigate(`/portal/${returnedClientId}/leads`);
      } else {
        setError(response?.data?.message || response?.data?.error || 'Login failed');
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.response?.statusText ||
        'Invalid username or password'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', backgroundColor: '#f8f9fa'
    }}>
      <div style={{
        background: 'white', borderRadius: '0px', padding: '40px',
        width: '100%', maxWidth: '400px',
        boxShadow: '0 10px 25px rgba(124,58,237,0.15)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '0px',
            background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px', fontSize: '24px'
          }}>🔐</div>
          <h1 style={{ fontSize: '22px', fontWeight: '800', color: '#1e293b' }}>Madocks Portal</h1>
          <p style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>Sign in to your account</p>
        </div>

        {error && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fee2e2',
            borderRadius: '0px', padding: '12px', marginBottom: '20px',
            color: '#dc2626', fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '6px' }}>
              Username
            </label>
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              placeholder="Enter your role username"
              required
              style={{
                width: '100%', padding: '10px 14px', border: '2px solid #f1f5f9',
                borderRadius: '0px', fontSize: '14px', outline: 'none',
                background: '#f8fafc', boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '6px' }}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                style={{
                  width: '100%', padding: '10px 40px 10px 14px', border: '2px solid #f1f5f9',
                  borderRadius: '0px', fontSize: '14px', outline: 'none',
                  background: '#f8fafc', boxSizing: 'border-box'
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                style={{
                  position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b',
                  zIndex: 5
                }}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%', padding: '12px',
              background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
              color: 'white', border: 'none', borderRadius: '0px',
              fontSize: '15px', fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
